import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const Stars = ({ value, size = 16 }) => (
    <span className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} size={size} className={s <= Math.round(value) ? "fill-gold text-gold" : "text-line"} />
        ))}
    </span>
);

export const ReviewsSection = ({ productId }) => {
    const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
    const [form, setForm] = useState({ name: "", rating: 5, text: "" });
    const [saving, setSaving] = useState(false);

    const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});

    useEffect(() => {
        load();
    }, [productId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Please add your name");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/products/${productId}/reviews`, { name: form.name, rating: form.rating, text: form.text });
            toast.success("Thank you — your review is live");
            setForm({ name: "", rating: 5, text: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="reviews-section" className="mt-20 border-t border-line pt-16">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Reviews</p>
                    <div className="mt-4 flex items-end gap-4">
                        <span className="font-serif text-5xl font-light text-gold" data-testid="reviews-average">{data.average || "—"}</span>
                        <div className="pb-1.5">
                            <Stars value={data.average} />
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-clay" data-testid="reviews-count">
                                {data.count} review{data.count === 1 ? "" : "s"}
                            </p>
                        </div>
                    </div>
                    <form onSubmit={submit} className="mt-10" data-testid="review-form">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Share your experience</p>
                        <div className="mt-3 flex gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => setForm((f) => ({ ...f, rating: s }))}
                                    data-testid={`review-star-${s}`}
                                    className="p-1 transition-transform hover:scale-110"
                                >
                                    <Star size={22} className={s <= form.rating ? "fill-gold text-gold" : "text-line"} />
                                </button>
                            ))}
                        </div>
                        <input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            data-testid="review-name-input"
                            placeholder="Your name"
                            className="mt-4 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                        />
                        <textarea
                            rows={3}
                            value={form.text}
                            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                            data-testid="review-text-input"
                            placeholder="What did you love about it?"
                            className="mt-3 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                        />
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="review-submit-button"
                            className="mt-4 w-full bg-espresso py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso disabled:opacity-60"
                        >
                            {saving ? "Posting…" : "Post review"}
                        </button>
                    </form>
                </div>
                <div className="lg:col-span-8">
                    {data.reviews.length === 0 ? (
                        <p className="font-serif text-xl italic text-clay lg:mt-12" data-testid="reviews-empty">No reviews yet — be the first to share.</p>
                    ) : (
                        <div className="flex flex-col">
                            {data.reviews.map((r) => (
                                <motion.div
                                    key={r.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5 }}
                                    className="border-b border-line py-6 first:pt-0"
                                    data-testid={`review-${r.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <Stars value={r.rating} size={14} />
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">{new Date(r.created_at).toLocaleDateString()}</span>
                                    </div>
                                    {r.text && <p className="mt-3 max-w-xl text-sm leading-relaxed text-clay">{r.text}</p>}
                                    <p className="mt-2 font-serif text-base italic">{r.name}</p>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};
